import is from '@sindresorhus/is';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

type SlotElements = {
    readonly topEl: HTMLDivElement | null;
};

type SlotSetters = {
    readonly setTopEl: (el: HTMLDivElement | null) => void;
};

const SlotElementsContext = createContext<SlotElements>({ topEl: null });
const SlotSettersContext = createContext<SlotSetters | null>(null);

export const LayoutSlotsProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const [topEl, setTopEl] = useState<HTMLDivElement | null>(null);

    const elements = useMemo(() => ({ topEl }), [topEl]);
    const setters = useMemo(() => ({ setTopEl }), []);

    return (
        <SlotSettersContext.Provider value={setters}>
            <SlotElementsContext.Provider value={elements}>{children}</SlotElementsContext.Provider>
        </SlotSettersContext.Provider>
    );
};

export const useLayoutSlotRefs = (): SlotSetters => {
    const setters = useContext(SlotSettersContext);
    if (is.null(setters)) {
        throw new Error('useLayoutSlotRefs must be used inside LayoutSlotsProvider');
    }
    return setters;
};

const LayoutSlotTop: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const { topEl } = useContext(SlotElementsContext);
    return is.null(topEl) ? null : createPortal(children, topEl);
};

export const LayoutSlot = {
    Top: LayoutSlotTop,
} as const;
